<template>
  <v-card class="h-100 py-6" rounded="lg" elevation="0">
    <div class="px-6" v-if="widgetData.loading">
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" />
      <v-skeleton-loader
        type="list-item-two-line@3,divider,list-item-two-line"
        class="h-100 bg-transparent"
      />
    </div>

    <div v-else-if="widgetData.data">
      <div class="d-flex justify-space-between mb-5 px-6">
        <div
          class="subtitle2 text-capitalize"
          v-text="$t('general.workspaces', { n: widgetData.data.total }, widgetData.data.total)"
        ></div>
        <div
          v-if="!workspacesAccessRestricted"
          @click="viewAll()"
          data-testid="workspace-stats-widget-view-all-link"
          class="body2 coro-link text-decoration-none"
          aria-label="View All Workspaces"
        >
          <div class="view-all-btn body2 coro-link item-clickable d-flex align-center">
            <span>{{ $t("general.viewAll") }}</span>
            <v-icon icon="$chevronRight" color="link" size="16" />
          </div>
        </div>
      </div>
      <template v-for="(item, index) in statusItems" :key="index">
        <v-divider
          v-if="'divider' in item"
          class="my-2"
          opacity="100"
          color="indigo-faint"
          :data-testid="item.testId"
        ></v-divider>
        <v-list-item v-else class="px-6" @click="navigateToWorkspaces(item.filters)">
          <div class="d-flex justify-space-between align-center py-2" :data-testid="item.testId">
            <div class="body2">
              {{ item.displayName }}
            </div>
            <div class="subtitle2" :class="item.extraClass">
              {{ item.count }}
            </div>
          </div>
        </v-list-item>
      </template>
    </div>
    <div
      v-else
      class="h-100 d-flex align-center justify-center flex-column px-6"
      data-testid="widget-error-state"
    >
      <v-icon icon="$alertTriangle" size="80"></v-icon>
      <span class="subtitle1 mb-4 text-center">
        {{ $t("mspDashboard.widgets.workspacesStatsWidget.error") }}
      </span>
      <div class="coro-link" @click="$emit('retry-fetch-data')">
        {{ $t("general.retry") }}
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import type { DataValue } from "@/types.ts";
import type { WorkspacesStats } from "@/_store/msp/msp-dashboard.module.ts";
import { RouteName } from "@/constants/routes.ts";
import { SubscriptionType } from "@/constants/workplaces.ts";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { defaultFiltersState, FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import { isMspAccessRestricted } from "@/_helpers/msp-permissions.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import { MspPortalScopeSection, MspRolePermissionScopes } from "@/constants/roles.ts";

export default defineComponent({
  props: {
    widgetData: {
      type: Object as PropType<DataValue<WorkspacesStats>>,
      required: true,
    },
  },
  emits: ["retry-fetch-data", "show-restricted-access-modal"],
  setup(props, { emit }) {
    const router = useRouter();
    const filtersStore = useFiltersStore();
    const i18n = useI18n();

    const statusItems = computed(() => {
      if (!props.widgetData.data) return [];
      return [
        {
          displayName: i18n.t(
            `mspDashboard.widgets.workspacesStatsWidget.statuses.${SubscriptionType.SUBSCRIPTION}`
          ),
          count: props.widgetData.data.subscriptionCount,
          extraClass: "",
          testId: "workspaces-stats-widget-subscription-item",
          filters: { subscriptionType: SubscriptionType.SUBSCRIPTION },
        },
        {
          displayName: i18n.t(
            `mspDashboard.widgets.workspacesStatsWidget.statuses.${SubscriptionType.INITIAL}`
          ),
          count: props.widgetData.data.newCount,
          extraClass: "",
          testId: "workspaces-stats-widget-new-item",
          filters: { subscriptionType: SubscriptionType.INITIAL },
        },
        {
          displayName: i18n.t(
            `mspDashboard.widgets.workspacesStatsWidget.statuses.${SubscriptionType.TRIAL}`
          ),
          count: props.widgetData.data.trialCount,
          extraClass: "",
          testId: "workspaces-stats-widget-trial-item",
          filters: { subscriptionType: SubscriptionType.TRIAL },
        },
        {
          displayName: i18n.t("mspDashboard.widgets.workspacesStatsWidget.statuses.nearExpiration"),
          count: props.widgetData.data.nearExpirationCount,
          extraClass: "text-red-dark",
          testId: "workspaces-stats-widget-near-expiration-item",
          filters: { subscriptionType: SubscriptionType.TRIAL, nearExpirationOnly: true },
        },
        { divider: true, testId: "workspaces-stats-widget-divider" },
        {
          displayName: i18n.t(
            `mspDashboard.widgets.workspacesStatsWidget.statuses.${SubscriptionType.FREEZE}`
          ),
          count: props.widgetData.data.inactiveCount,
          extraClass: "text-indigo-medium",
          testId: "workspaces-stats-widget-freeze-item",
          filters: { subscriptionType: SubscriptionType.FREEZE },
        },
      ];
    });

    const workspacesAccessRestricted = computed(() => {
      return isMspAccessRestricted(
        MspRolePermissionScopes.MSP_PORTAL_SCOPE,
        MspPortalScopeSection.MSP_WORKSPACES
      );
    });
    const navigateToWorkspaces = async ({
      subscriptionType,
      nearExpirationOnly,
    }: {
      subscriptionType: SubscriptionType;
      nearExpirationOnly?: boolean;
    }) => {
      if (workspacesAccessRestricted.value) {
        emit("show-restricted-access-modal");
        return;
      }
      const filters = {
        ...defaultFiltersState[FilterContext.MSP],
        subscriptionTypes: subscriptionType ? [subscriptionType] : [],
        nearExpirationOnly: nearExpirationOnly ?? false,
      };
      filtersStore.setFilters(FilterContext.MSP, filters);
      await router.push({ name: RouteName.MSP_WORKSPACES_LIST_TAB });
      if (nearExpirationOnly) {
        trackGtmEvent(GtmAction.MSP_DASHBOARD_WIDGET, GTMParams.WORKSPACE_WIDGET_EXPIRING_SOON);
      } else {
        trackGtmEvent(
          GtmAction.MSP_DASHBOARD_WIDGET,
          `${GTMParams.WORKSPACE_WIDGET} - ${startCase(subscriptionType)}`
        );
      }
    };

    const viewAll = async () => {
      await router.push({ name: RouteName.MSP_WORKSPACES_PAGE });
      trackGtmEvent(GtmAction.MSP_DASHBOARD_WIDGET, GTMParams.WORKSPACE_WIDGET_ALL);
    };

    return {
      workspacesAccessRestricted,
      RouteName,
      SubscriptionType,
      statusItems,
      navigateToWorkspaces,
      viewAll,
    };
  },
});
</script>
<style lang="scss">
.view-all-btn {
  text-decoration: none;

  .icon-chevron-right-xs {
    text-decoration: none;
    &:before {
      color: rgb(var(--v-theme-anchor-base));
    }
  }

  &:hover span {
    text-decoration: underline; /* Underline only the text */
  }
}

.icon-alert-triangle-l:before {
  color: rgb(var(--v-theme-indigo-light)) !important;
}
</style>
